import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { Subscription, timer } from 'rxjs';
import { filter, switchMap, take, takeWhile } from 'rxjs/operators';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { BarLineComboChartComponent } from '../../charts/bar-line-combo-chart/bar-line-combo-chart.component';
import { MatAngularModule } from '../../modules/mat-angular.module';
import { GenericTemplateCommonService } from '../../components/template-ai-common/services/generic-template-common.service';
import { AuthService, UserSettings } from '../../../services/auth.service';
import { GenericCommanService } from '../../../services/generic-common.service';
import { ProjectHistoryService } from '../../../services/project-history.service';
import { TaskProgressWebSocketService } from '../../../services/task-progress-websocket.service';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'app-generic-ai-content-index',
  standalone: true,
  imports: [
    CommonModule,
    NgxChartsModule,
    MatAngularModule,
    // CitationCardComponent,
    BarLineComboChartComponent,
  ],
  templateUrl: './generic-ai-content-index.component.html',
  styleUrl: './generic-ai-content-index.component.scss'
})
export class GenericAiContentIndexComponent implements OnInit, OnDestroy {
  // Core data properties
  aiContentIndexResponse: any = null;
  brands: any[] = [];
  context: any = null;

  // Loading and error state

  // Chart data
  barChartData: any[] = [];
  // Combo chart data (first chart: content domains)
  results: any[] = [];
  lineChart: any[] = [];
  overallScore: number = 0;

  // Second chart: content quality metrics (Topic Authority, AI Readability, etc.) – only when API provides data
  resultsQuality: any[] = [];
  lineChartQuality: any[] = [];
  /** True only when API returned content_quality_metrics; chart is hidden otherwise. */
  hasContentQualityMetricsFromApi = false;
  
  // Analysis data
  strengths: string[] = [];
  weaknesses: string[] = [];
  evidenceSources: any[] = [];
  recommendations: any[] = [];

  // Chart options
  chartView: [number, number] = [900, 350];
  colorScheme: any = {
    domain: ['#5AA454', '#A10A28', '#C7B42C', '#AAAAAA', '#FF7F0E', '#1F77B4']
  };
  comboBarScheme: any = {
    name: 'singleLightBlue',
    selectable: true,
    group: 'ordinal',
    domain: ['#1976d2']
  };
  lineChartScheme: any = {
    name: 'coolthree',
    selectable: true,
    group: 'ordinal',
    domain: ['#01579b', '#7aa3e5', '#a8385d', '#00bfa5']
  };

  // Subscriptions
  private subscriptions: Subscription[] = [];

  // Section reload (admin + setting)
  isAdmin = false;
  showSectionReloadButton = false;
  reloadingSection = false;

  private authService = inject(AuthService);
  private genericCommonService = inject(GenericCommanService);
  private projectHistoryService = inject(ProjectHistoryService);
  private taskProgressWs = inject(TaskProgressWebSocketService);
  private snackBar = inject(MatSnackBar);

  constructor(
    private templateCommonService: GenericTemplateCommonService,
    private cdr: ChangeDetectorRef
  ) {
    this.updateChartView();
  }




  ngOnInit(): void {
    // Subscribe to AI Content Index response
    const analysisSub = this.templateCommonService.aiContentIndexResponse$.subscribe((response: any) => {
      if (response) {
        this.aiContentIndexResponse = response;
        this.processAiContentIndexData(response);
        this.cdr.detectChanges();
      }
    });

    // Subscribe to filterData changes to detect competitor additions/removals
    let previousEntityNames: string[] = [];
    // Initialize from current filterData if available
    const currentFilterData = this.templateCommonService.getCurrentFilterData();
    if (currentFilterData && currentFilterData.selectedEntities) {
      previousEntityNames = currentFilterData.selectedEntities.map((e: any) => e.name || e).sort();
    }
    const filterDataSub = this.templateCommonService.filterData$.subscribe((filterData: any) => {
      // Do not auto-trigger API on Enter; only track names and wait for Analyze/Compare
      if (filterData && filterData.selectedEntities) {
        const currentEntityNames = filterData.selectedEntities.map((e: any) => e.name || e).sort();
        previousEntityNames = currentEntityNames;
      }
    });

    // Add subscriptions to array
    this.subscriptions.push(analysisSub, filterDataSub);

    // Load data when component initializes
    this.loadAiContentIndexData();

    // Section reload visibility (admin + user setting)
    this.isAdmin = this.authService.isAdmin();
    if (this.isAdmin) {
      const settingsSub = this.authService.getUserSettings().subscribe((s: UserSettings) => {
        this.showSectionReloadButton = s.show_section_reload_button ?? false;
        this.cdr.detectChanges();
      });
      this.subscriptions.push(settingsSub);
    }

    // Update chart view on window resize
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateChartView, { passive: true });
    }
  }

  ngOnDestroy(): void {
    console.log('🗑️ AI Content Index: Destroying component and clearing memory...');
    
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
    
    // Don't clear service-level cache - data should persist across tab switches
    // The service manages the data and tabs subscribe to observables
    
    // Clear all local data references
    this.aiContentIndexResponse = null;
    this.brands = [];
    this.barChartData = [];
    this.results = [];
    this.lineChart = [];
    this.strengths = [];
    this.weaknesses = [];
    this.evidenceSources = [];
    this.recommendations = [];
    this.overallScore = 0;
    
    // Remove resize listener
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateChartView);
    }
    
    console.log('✅ AI Content Index: Component destroyed and memory cleared');
  }

  loadAiContentIndexData(): void {
    // Check if we already have a response (data might have been loaded before component initialized)
    const currentResponse = this.templateCommonService.getCurrentAiContentIndexResponse();
    if (currentResponse) {
      console.log('AI Content Index: Found existing response, processing immediately');
      this.aiContentIndexResponse = currentResponse;
      this.processAiContentIndexData(currentResponse);
      this.cdr.detectChanges();
    } else {
      // No data yet - subscription in ngOnInit() will handle it when data arrives
      console.log('AI Content Index: No data yet, waiting for subscription to receive data');
    }
  }

  onReload(): void {
    this.templateCommonService.triggerTabReload('ai-content-index', true);
  }

  /** Handle completed/failed status from either poll or WebSocket (run once). */
  private handleReloadStatus(
    projectdataId: string,
    status: string,
    error?: string
  ): void {
    if (status === 'completed') {
      this.projectHistoryService.getVersionData(projectdataId).subscribe({
        next: (version: { data?: Record<string, unknown> }) => {
          const tabData = version.data?.['ai-content-index'];
          if (tabData) {
            this.templateCommonService.updateAiContentIndexResponse(tabData);
          }
          this.reloadingSection = false;
          this.cdr.detectChanges();
          this.snackBar.open('AI Content Index section updated', 'Close', { duration: 3000 });
        },
        error: () => {
          this.reloadingSection = false;
          this.cdr.detectChanges();
          this.snackBar.open('Section updated but failed to refresh view', 'Close', { duration: 4000 });
        }
      });
    } else if (status === 'failed') {
      this.reloadingSection = false;
      this.cdr.detectChanges();
      this.snackBar.open(error || 'Section reload failed', 'Close', { duration: 5000 });
    }
  }

  /** Reload only this section (admin): run AI Content Index in background and merge into projectdata. */
  reloadSection(): void {
    if (this.reloadingSection) return;
    const { projectId, projectdataId } = this.templateCommonService.getCurrentProjectContext();
    if (!projectId || !projectdataId) {
      this.snackBar.open('Open a project version to use section reload', 'Close', { duration: 4000 });
      return;
    }
    this.reloadingSection = true;
    this.cdr.detectChanges();
    this.genericCommonService.reloadAiContentIndexSection(projectId, projectdataId).subscribe({
      next: (res: { task_id: string; message?: string }) => {
        const taskId = res.task_id;
        let handled = false;
        const doHandle = (status: string, err?: string) => {
          if (handled) return;
          handled = true;
          pollSub?.unsubscribe();
          wsSub?.unsubscribe();
          this.handleReloadStatus(projectdataId, status, err);
        };

        let pollSub: Subscription | undefined;
        const wsSub = this.taskProgressWs.useWebSocket
          ? this.taskProgressWs.messagesByPath('ai_content_index_reload').pipe(
              filter((m) => (m.data as { task_id?: string })?.task_id === taskId),
              take(1)
            ).subscribe((msg) => {
              const d = msg.data as { status?: string; error?: string };
              const s = d?.status ?? '';
              if (s === 'completed' || s === 'failed') {
                doHandle(s, d?.error);
              }
            })
          : undefined;

        const poll$ = timer(0, 3000).pipe(
          switchMap(() => this.genericCommonService.getReloadAiContentIndexStatus(taskId)),
          takeWhile((s: { status: string; error?: string }) => s.status === 'running', true)
        );
        pollSub = poll$.subscribe({
          next: (status: { status: string; error?: string }) => {
            if (status.status === 'completed' || status.status === 'failed') {
              doHandle(status.status, status.error);
            }
          },
          error: () => {
            if (!handled) {
              handled = true;
              wsSub?.unsubscribe();
              this.reloadingSection = false;
              this.cdr.detectChanges();
              this.snackBar.open('Failed to get reload status', 'Close', { duration: 4000 });
            }
          }
        });
      },
      error: (err: { error?: { detail?: string; message?: string } }) => {
        this.reloadingSection = false;
        this.cdr.detectChanges();
        const msg = err?.error?.detail || err?.error?.message || 'Failed to start section reload';
        this.snackBar.open(msg, 'Close', { duration: 5000 });
      }
    });
  }

  retryLoad(): void {
    this.onReload();
  }

  hasData(): boolean {
    return !!(this.aiContentIndexResponse && (this.brands?.length > 0 || this.barChartData?.length > 0));
  }

  private processAiContentIndexData(data: any): void {
    try {
      // Handle nested response structure
      let processedData = data;
      if (data.structured_data) {
        processedData = data.structured_data;
      }
      // Legacy support: if response field exists (old cached data), try to parse it
      else if (data.response && typeof data.response === 'string' && data.response.trim().startsWith('{')) {
        try {
          processedData = JSON.parse(data.response);
        } catch (e) {
          console.warn('⚠️ AI Content Index: Failed to parse response JSON:', e);
          processedData = data;
        }
      }

      // Check if this is a brand performance response and convert it
      if (processedData.brands && processedData.brands[0] && processedData.brands[0].brand_score && !processedData.brands[0].content_domains) {
        processedData = this.convertBrandPerformanceToContentIndex(processedData);
      }

      // Extract main data
      this.brands = processedData.brands || [];
      this.context = processedData.context || null;
      this.aiContentIndexResponse = processedData;

      // Process chart data
      if (processedData.chart_data && processedData.chart_data.data) {
        this.barChartData = processedData.chart_data.data.map((brand: any) => ({
          name: brand.name,
          series: brand.series.map((item: any) => ({
            name: item.name,
            value: item.value
          }))
        }));
      } else {
        this.generateChartDataFromBrands();
      }

      // Build combo chart data (YOU as bars, competitors as lines)
      this.prepareComboChartData(processedData);

      // Build second chart: six content quality metrics (your brand = bars, competitors = lines)
      this.prepareQualityComboChartData(processedData);

      // Process recommendations and evidence sources
      this.recommendations = processedData.recommendations || [];
      this.evidenceSources = processedData.evidence_sources || [];

      // Overall score: calculated on frontend from API brands only (not from content_quality_metrics).
      // When quality-metrics section is hidden (API has no content_quality_metrics), this score is unchanged.
      if (this.brands && this.brands.length > 0) {
        this.overallScore = Math.round(
          this.brands.reduce((sum: number, brand: any) => sum + (brand.overall_content_score || 0), 0) / this.brands.length
        );
      }

      // Extract strengths and weaknesses
      this.strengths = [];
      this.weaknesses = [];
      this.brands.forEach((brand: any) => {
        if (brand.content_strengths) {
          this.strengths.push(...brand.content_strengths);
        }
        if (brand.content_weaknesses) {
          this.weaknesses.push(...brand.content_weaknesses);
        }
      });

      // Remove duplicates (case-insensitive)
      const seenStrengths = new Set<string>();
      this.strengths = this.strengths.filter(s => {
        const lower = s.toLowerCase();
        if (seenStrengths.has(lower)) return false;
        seenStrengths.add(lower);
        return true;
      });
      const seenWeaknesses = new Set<string>();
      this.weaknesses = this.weaknesses.filter(w => {
        const lower = w.toLowerCase();
        if (seenWeaknesses.has(lower)) return false;
        seenWeaknesses.add(lower);
        return true;
      });
      
    } catch (error) {
      console.error('Failed to process AI Content Index data:', error);
    }

    this.cdr.markForCheck();
  }

  private convertBrandPerformanceToContentIndex(brandPerformanceData: any): any {
    
    const convertedData = {
      ...brandPerformanceData,
      brands: brandPerformanceData.brands.map((brand: any) => ({
        ...brand,
        content_domains: [
          {
            domain: "Website Content",
            influence_score: brand.brand_score || 0,
            citation_count: 0,
            social_media_presence: brand.brand_score || 0,
            research_impact: brand.brand_score || 0,
            industry_collaborations: brand.brand_score || 0,
            content_quality: brand.brand_score || 0,
            seo_optimization: brand.brand_score || 0,
            user_engagement: brand.brand_score || 0,
            brand_visibility: brand.brand_score || 0,
            thought_leadership: brand.brand_score || 0
          },
          {
            domain: "Social Media",
            influence_score: brand.brand_score || 0,
            citation_count: 0,
            social_media_presence: brand.brand_score || 0,
            research_impact: brand.brand_score || 0,
            industry_collaborations: brand.brand_score || 0,
            content_quality: brand.brand_score || 0,
            seo_optimization: brand.brand_score || 0,
            user_engagement: brand.brand_score || 0,
            brand_visibility: brand.brand_score || 0,
            thought_leadership: brand.brand_score || 0
          },
          {
            domain: "Digital Media",
            influence_score: brand.brand_score || 0,
            citation_count: 0,
            social_media_presence: brand.brand_score || 0,
            research_impact: brand.brand_score || 0,
            industry_collaborations: brand.brand_score || 0,
            content_quality: brand.brand_score || 0,
            seo_optimization: brand.brand_score || 0,
            user_engagement: brand.brand_score || 0,
            brand_visibility: brand.brand_score || 0,
            thought_leadership: brand.brand_score || 0
          },
          {
            domain: "YouTube Content",
            influence_score: brand.brand_score || 0,
            citation_count: 0,
            social_media_presence: brand.brand_score || 0,
            research_impact: brand.brand_score || 0,
            industry_collaborations: brand.brand_score || 0,
            content_quality: brand.brand_score || 0,
            seo_optimization: brand.brand_score || 0,
            user_engagement: brand.brand_score || 0,
            brand_visibility: brand.brand_score || 0,
            thought_leadership: brand.brand_score || 0
          },
          {
            domain: "Thought Leadership",
            influence_score: brand.brand_score || 0,
            citation_count: 0,
            social_media_presence: brand.brand_score || 0,
            research_impact: brand.brand_score || 0,
            industry_collaborations: brand.brand_score || 0,
            content_quality: brand.brand_score || 0,
            seo_optimization: brand.brand_score || 0,
            user_engagement: brand.brand_score || 0,
            brand_visibility: brand.brand_score || 0,
            thought_leadership: brand.brand_score || 0
          }
        ],
        overall_content_score: brand.brand_score,
        content_strengths: brand.key_strengths || [],
        content_weaknesses: brand.key_weaknesses || [],
        content_opportunities: brand.strategic_recommendations?.map((rec: any) => rec.recommendation) || [],
        key_insights: [
          "Strong digital content foundation",
          "Moderate social media engagement",
          "Potential for growth in video content"
        ]
      })),
      chart_data: {
        title: "AI Content Index Analysis",
        data: brandPerformanceData.brands.map((brand: any) => ({
          name: brand.name,
          series: [
            { name: "Website Content", value: brand.brand_score || 0 },
            { name: "Social Media", value: brand.brand_score || 0 },
            { name: "Digital Media", value: brand.brand_score || 0 },
            { name: "YouTube Content", value: brand.brand_score || 0 },
            { name: "Thought Leadership", value: brand.brand_score || 0 }
          ]
        }))
      },
      recommendations: brandPerformanceData.brands[0]?.strategic_recommendations?.map((rec: any) => ({
        category: "content_improvement",
        issue: rec.recommendation,
        recommendation: rec.recommendation,
        priority: rec.priority,
        priority_score: rec.priority === 'High' ? 3 : rec.priority === 'Medium' ? 2 : 1,
        expected_impact: rec.expected_impact,
        target_score: Math.min(95, brandPerformanceData.brands[0].brand_score + 5),
        brand: brandPerformanceData.brands[0].name
      })) || [],
      evidence_sources: this.convertCitationsToEvidenceSources(brandPerformanceData.context?.citations || [])
    };

    return convertedData;
  }

  private convertCitationsToEvidenceSources(citations: any[]): any[] {
    return citations.map(citation => ({
      source: citation.source,
      url: citation.url,
      title: citation.title,
      snippet: citation.snippet,
      confidence: citation.confidence,
      category: "digital_presence",
      content_domain: "Website Content"
    }));
  }

  private generateChartDataFromBrands(): void {
    if (this.brands && this.brands.length > 0) {
      this.barChartData = this.brands.map((brand: any) => ({
        name: brand.name,
        series: brand.content_domains?.map((domain: any) => ({
          name: domain.domain,
          value: domain.influence_score || domain.content_score || 75
        })) || [
          { name: "Website Content", value: 75 },
          { name: "Social Media", value: 70 },
          { name: "Digital Media", value: 72 },
          { name: "YouTube Content", value: 68 },
          { name: "Thought Leadership", value: 80 }
        ]
      }));
    }
  }

  private prepareComboChartData(processedData: any): void {
    // Determine your brand
    const yourBrandName = processedData?.metadata?.your_brand || this.brands[0]?.name || (this.barChartData[0]?.name);

    // Find YOU and competitors in barChartData (which already holds categories/series)
    const you = (this.barChartData || []).find(b => b.name === yourBrandName) || this.barChartData[0];
    const competitors = (this.barChartData || []).filter(b => b.name !== (you?.name));

    // Prepare bars: YOU categories
    this.results = you ? you.series.map((s: any) => ({ name: s.name, value: s.value || 0 })) : [];

    // Prepare lines: each competitor as a series across same categories
    this.lineChart = competitors.map((comp: any) => ({
      name: comp.name,
      series: comp.series.map((s: any) => ({ name: s.name, value: s.value || 0 }))
    }));
  }

  /** If API has content_quality_metrics → show chart (labels from API); else hide. Same pattern as first chart. */
  private prepareQualityComboChartData(processedData: any): void {
    const rows = processedData?.content_quality_metrics;
    const hasData = Array.isArray(rows) && rows.length > 0;
    this.hasContentQualityMetricsFromApi = !!hasData;
    if (!hasData) {
      this.resultsQuality = [];
      this.lineChartQuality = [];
      return;
    }

    // Derive metric keys from first row (same as first chart: labels from API)
    const first = rows[0];
    const metricsObj = first?.metrics && typeof first.metrics === 'object' ? first.metrics : first;
    const metricKeys = Object.keys(metricsObj || {}).filter(
      k => k !== 'brand_name' && k !== 'name'
    );
    if (metricKeys.length === 0) {
      this.hasContentQualityMetricsFromApi = false;
      this.resultsQuality = [];
      this.lineChartQuality = [];
      return;
    }

    const yourBrandName = processedData?.metadata?.your_brand || this.brands[0]?.name;
    const qualityByBrand: { name: string; series: { name: string; value: number }[] }[] = rows.map((row: any) => {
      const name = row.brand_name ?? row.name ?? '';
      const metrics = row.metrics && typeof row.metrics === 'object' ? row.metrics : row;
      const series = metricKeys.map(key => ({
        name: this.formatCategoryName(key),
        value: typeof metrics[key] === 'number' ? metrics[key] : Number(metrics[key]) || 0
      }));
      return { name, series };
    });

    const youQuality = qualityByBrand.find(b => b.name === yourBrandName) || qualityByBrand[0];
    const competitorsQuality = qualityByBrand.filter(b => b.name !== (youQuality?.name));

    this.resultsQuality = youQuality ? youQuality.series.map(s => ({ name: s.name, value: s.value })) : [];
    this.lineChartQuality = competitorsQuality.map(c => ({
      name: c.name,
      series: c.series.map(s => ({ name: s.name, value: s.value }))
    }));
  }

  // Update chart view based on window size
  private updateChartView = (): void => {
    if (typeof window === 'undefined') {
      this.chartView = [900, 350];
      return;
    }

    const width = window.innerWidth;
    if (width < 576) {
      this.chartView = [250, 250];
    } else if (width < 768) {
      this.chartView = [900, 350];
    } else if (width < 1024) {
      this.chartView = [500, 400];
    } else {
      this.chartView = [900, 350];
    }
    this.cdr.markForCheck();
  }

  formatCategoryName(category: string): string {
    return category
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getScoreColor(score: number): string {
    if (score >= 80) return '#5AA454'; // Green
    if (score >= 60) return '#FF7F0E'; // Orange
    return '#A10A28'; // Red
  }

  getScoreClass(score: number): string {
    if (score >= 80) return 'score-excellent';
    if (score >= 60) return 'score-good';
    return 'score-poor';
  }

  onChartSelect(event: any): void {
    // Chart selection handler
  }
}
